import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { MatCardModule } from '@angular/material/card';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatPaginatorModule } from '@angular/material/paginator';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CdkDropList, CdkDrag} from '@angular/cdk/drag-drop';

import { AppComponent } from './app.component';
import { ExcelToTableComponent } from './shared/components/excel-to-table/excel-to-table.component';
import { DecimalPipePipe } from './shared/pipes/decimal-pipe.pipe'
import { DecimalPipe } from '@angular/common';
import { RateAgreementComponent } from './pages/rate-agreement/rate-agreement.component';
import { LoginComponent } from './pages/login/login.component';
import { CarriersComponent } from './pages/carriers/carriers.component';
import { BidPacksComponent } from './pages/bid-packs/bid-packs.component';
import { EditRateAgreementComponent } from './pages/edit-rate-agreement/edit-rate-agreement.component';
import { FuelEscalatorComponent } from './pages/fuel-escalator/fuel-escalator.component';
import { ContactComponent } from './pages/contact/contact.component';
import { MaintananceComponent } from './pages/maintanance/maintanance.component';
import { HelpScreenComponent } from './pages/help-screen/help-screen.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { InputFieldsComponent } from './shared/components/input-fields/input-fields.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { RateAgreementTableComponent } from './shared/components/rate-agreement-table/rate-agreement-table.component';
import { BreadcrumbComponent } from './shared/components/breadcrumb/breadcrumb.component';
import { AppService } from './app.service';
import { AgreementInformationComponent } from './shared/components/agreement-information/agreement-information.component';
import { AgreementRateTableComponent } from './shared/components/agreement-rate-table/agreement-rate-table.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ToggleButtonComponent } from './shared/components/form-fields/toggle-button/toggle-button.component';

//----MFA Authentication ------------------
import { MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { FilerOverlayComponent } from './shared/components/filer-overlay/filer-overlay.component';

// Import the Azure AD B2C configuration 
import { msalConfig, protectedResources } from './shared/config/auth.config';
import { ImportDialogComponent } from './shared/components/import-dialog/import-dialog.component';
import { FileErrorPopupComponent } from './shared/components/file-error-popup/file-error-popup.component';
import { WarningCalculationComponent } from './shared/components/warning-calculation/warning-calculation.component';
import { ContactTableComponent } from './shared/components/contact-table/contact-table.component';
import { AddEditContactComponent } from './pages/add-edit-contact/add-edit-contact.component';
import { CarrierTableComponent } from './shared/components/carrier-table/carrier-table.component';
import { EditCarrierComponent } from './pages/edit-carrier/edit-carrier.component';
import { FuelEscalatorTableComponent } from './shared/components/fuel-escalator-table/fuel-escalator-table.component';
import { EditFuelEscalatorComponent } from './pages/edit-fuel-escalator/edit-fuel-escalator.component';
import { BidpackTableComponent } from './shared/components/bidpack-table/bidpack-table.component';
import { EditBidpackComponent } from './pages/edit-bidpack/edit-bidpack.component';
import { AdditionalChargesComponent } from './shared/components/additional-charges/additional-charges.component';
import { HttpTokenInterceptor } from './shared/interceptors/http-token.interceptor';
import { HttpHeaderInterceptor } from './shared/interceptors/http-header.interceptor';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { ChartComponent } from './pages/chart/chart.component';
import { AgChartsAngular } from 'ag-charts-angular';
import { LtlMatrixComponent } from './shared/components/tables/ltl-matrix/ltl-matrix.component';
import { FtlMatrixComponent } from './shared/components/tables/ftl-matrix/ftl-matrix.component';
import { LoadingSpinnerComponent } from './shared/components/loading-spinner/loading-spinner.component';
import { DeleteWarningComponent } from './shared/components/delete-warning/delete-warning.component';
import { AuthComponent } from './pages/auth/auth.component';
import { MatrixComponent } from './shared/components/tables/matrix/matrix.component';
import { NgxFileDropModule } from 'ngx-file-drop';
import { ExcelService } from './shared/services/excel.service';
import { AddRateTableComponent } from './shared/components/add-rate-table/add-rate-table.component';
import { FilesizePipe } from './shared/pipes/filesize.pipe';
import { FixedFtlComponent } from './shared/components/tables/fixed-ftl/fixed-ftl.component';
import { ExportToPdfComponent } from './shared/components/export-to-pdf/export-to-pdf.component';
import { ExportPdfTableComponent } from './export-pdf-table/export-pdf-table.component';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, MatNativeDateModule } from '@angular/material/core';
import { DateFilterDirective } from './shared/directive/date-filter.directive';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';

import { AddDialogComponent } from './shared/add-dialog/add-dialog.component';
import { UnsavedAlertComponent } from './shared/components/unsaved-alert/unsaved-alert.component';
import { authGuard } from './auth.guard';
import { AuthService } from './auth.service';
import { ExportPdfFixedComponent } from './shared/components/export-pdf-fixed/export-pdf-fixed.component';
import { ExportPdfLtlComponent } from './shared/components/export-pdf-ltl/export-pdf-ltl.component';
import { ExportToExcelComponent } from './shared/components/export-to-excel/export-to-excel.component';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { ValidationPopupComponent } from './shared/components/validation-popup/validation-popup.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { MatDatepickerModule } from '@angular/material/datepicker';

// import { GridModule, ExcelModule } from "@progress/kendo-angular-grid";
import { 
	IgxDateRangePickerModule,
	IgxInputGroupModule
 } from "igniteui-angular";
import { ExportAditionalChargesComponent } from './shared/components/export-aditional-charges/export-aditional-charges.component';

export const MY_DATE_FORMATS: MatDateFormats = {
    parse: {
      dateInput: 'DD/MM/YYYY', // Format used when parsing user input
    },
    display: {
      dateInput: 'DD/MM/YYYY', // Format shown in the input field
      monthYearLabel: 'MMM YYYY',
      dateA11yLabel: 'DD/MM/YYYY',
      monthYearA11yLabel: 'MMMM YYYY',
    }
  };

@NgModule({
    declarations: [
        AppComponent,
        ExcelToTableComponent,
        DecimalPipePipe,
        DateFilterDirective,
        FilesizePipe,
        RateAgreementComponent,
        LoginComponent,
        CarriersComponent,
        BidPacksComponent,
        EditRateAgreementComponent,
        FuelEscalatorComponent,
        ContactComponent,
        MaintananceComponent,
        HelpScreenComponent,
        PageNotFoundComponent,
        InputFieldsComponent,
        UnsavedAlertComponent,
        HeaderComponent,
        FooterComponent,
        ExportPdfFixedComponent,
        ExportPdfLtlComponent,
        RateAgreementTableComponent,
        BreadcrumbComponent,
        AgreementInformationComponent,
        AgreementRateTableComponent,
        ToggleButtonComponent,
        FilerOverlayComponent,
        ImportDialogComponent,
        FileErrorPopupComponent,
        WarningCalculationComponent,
        ContactTableComponent,
        AddEditContactComponent,
        CarrierTableComponent,
        EditCarrierComponent,
        FuelEscalatorTableComponent,
        EditFuelEscalatorComponent, 
        BidpackTableComponent,
        EditBidpackComponent,
        AdditionalChargesComponent,
        LandingPageComponent,
        ChartComponent,
        LtlMatrixComponent,
        FtlMatrixComponent,
        FixedFtlComponent,
        ExportToPdfComponent,
        LoadingSpinnerComponent,
        DeleteWarningComponent,
        ExportPdfTableComponent,
        AuthComponent,
        MatrixComponent,
        AddRateTableComponent,
        AddDialogComponent,
        ExportToExcelComponent,
        ValidationPopupComponent,
        ExportAditionalChargesComponent
    ],
    providers: [AppService, DecimalPipe, DecimalPipePipe, DatePipe, provideAnimationsAsync(),
        ExcelService,
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, // This sets the locale to 'en-GB' for day/month/year format
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: HTTP_INTERCEPTORS, useClass: HttpTokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpHeaderInterceptor, multi: true },
        { provide: 'authGuard', useFactory: () => authGuard },
        AuthService,
        MatDatepickerModule
    ],
    bootstrap: [AppComponent, ],
    imports: [
        BrowserModule,
        BsDatepickerModule.forRoot(),
        AppRoutingModule,
        BrowserAnimationsModule,
        MatButtonModule,
        MatInputModule,
        MatCardModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatDialogModule,
        MatCheckboxModule,
        MatButtonModule,
        MatGridListModule,
        MatToolbarModule,
        MatIconModule,
        MatBadgeModule,
        MatMenuModule,
        MatSelectModule,
        MatTabsModule,
        MatListModule,
        FormsModule,
        MatRadioModule,
        CommonModule,
        NgClass,
        MatTooltipModule,
        MatSnackBarModule,
        MatPaginatorModule,
        HttpClientModule,
        MatSortModule,
        CdkDropList,
        CdkDrag,
        MatDatepickerModule,
        AgChartsAngular,
        MatSelectModule,
        MatCheckboxModule,
        NgxFileDropModule,
        MatNativeDateModule,
        ExcelExportModule,
        IgxDateRangePickerModule,
        IgxInputGroupModule,
        NgxDaterangepickerMd.forRoot(),
        
        // GridModule, ExcelModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }
